import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import BiomarkerForm from '@/components/BiomarkerForm';
import ProtocolCard from '@/components/ProtocolCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { MATCH, STATE } from '@/constants/testIds/healthspan';
import { FileDown, Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import TierGateCTA from '@/components/TierGateCTA';

const INITIAL = { name: 'Baseline' };

export default function Match() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [biomarkers, setBiomarkers] = useState(INITIAL);
  const [mechanism, setMechanism] = useState('');
  const [interventionType, setInterventionType] = useState('');
  const [meta, setMeta] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get('/meta').then(r => setMeta(r.data)).catch(()=>{}); }, []);

  // Preload saved profile if ?profile=<id>
  useEffect(() => {
    const pid = params.get('profile');
    if (pid && user) {
      (async () => {
        try {
          const { data } = await api.get('/profiles');
          const found = data.find(p => p.id === pid);
          if (found) { setBiomarkers(found); toast.info(`Loaded profile: ${found.name}`); }
        } catch {}
      })();
    }
  }, [params, user]);

  const cleanedBiomarkers = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(biomarkers)) {
      if (v === '' || v === undefined) continue;
      out[k] = v;
    }
    if (!out.name) out.name = 'Baseline';
    return out;
  }, [biomarkers]);

  const runMatch = async () => {
    setLoading(true); setResults(null);
    try {
      const { data } = await api.post('/match', {
        biomarkers: cleanedBiomarkers,
        mechanism: mechanism || null,
        intervention_type: interventionType || null,
        limit: 20,
      });
      setResults(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Match failed');
    } finally { setLoading(false); }
  };

  const saveProfile = async () => {
    if (!user) { toast.info('Sign in to save profiles'); return; }
    setSavingProfile(true);
    try {
      await api.post('/profiles', cleanedBiomarkers);
      toast.success('Profile saved to your dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingProfile(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.post('/match/export.csv', {
        biomarkers: cleanedBiomarkers,
        mechanism: mechanism || null,
        intervention_type: interventionType || null,
        limit: 50,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'protocol_matches.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 401) {
        toast.error('CSV export requires Researcher tier');
      } else {
        toast.error('Export failed');
      }
    } finally { setExporting(false); }
  };

  const labelize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Biomarker match</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">Run a deterministic protocol match</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-fg)] max-w-3xl">All fields are optional. Provide as many or as few as you have. Scoring blends evidence tier, mechanism relevance and biomarker fit — weights are admin-tunable and shown per result.</p>
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="space-y-4" data-testid={MATCH.form}>
          <Card className="p-4 sm:p-5 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
            <div className="grid sm:grid-cols-[1fr_1fr_1fr] gap-3">
              <div className="space-y-1">
                <Label>Profile name</Label>
                <Input value={biomarkers.name || ''} onChange={(e)=>setBiomarkers(b=>({...b,name:e.target.value}))} />
              </div>
              <div className="space-y-1">
                <Label>Mechanism filter</Label>
                <Select value={mechanism} onValueChange={(v)=>setMechanism(v==='__all'?'':v)}>
                  <SelectTrigger data-testid={MATCH.mechanismFilter}><SelectValue placeholder="All mechanisms" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All mechanisms</SelectItem>
                    {(meta?.mechanisms || []).map(m => <SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Intervention type</Label>
                <Select value={interventionType} onValueChange={(v)=>setInterventionType(v==='__all'?'':v)}>
                  <SelectTrigger data-testid={MATCH.interventionFilter}><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All types</SelectItem>
                    {(meta?.intervention_types || []).map(m => <SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <BiomarkerForm value={biomarkers} onChange={setBiomarkers} />

          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid={MATCH.submit} onClick={runMatch} disabled={loading} className="bg-[color:var(--brand-primary)] text-[color:var(--brand-primary-fg)] hover:bg-[color:var(--brand-primary-2)]">
              <Sparkles className="mr-2 h-4 w-4" /> {loading ? 'Matching…' : 'Run match'}
            </Button>
            {user && <Button data-testid={MATCH.saveProfile} variant="outline" onClick={saveProfile} disabled={savingProfile}><Save className="mr-2 h-4 w-4" /> {savingProfile ? 'Saving…' : 'Save profile'}</Button>}
            {results && results.matches.length > 0 && (
              user?.role === 'researcher' || user?.role === 'admin' ? (
                <Button data-testid={MATCH.exportCsv} variant="outline" onClick={exportCsv} disabled={exporting}><FileDown className="mr-2 h-4 w-4" /> {exporting ? 'Exporting…' : 'Export CSV'}</Button>
              ) : (
                <div className="text-xs text-[color:var(--muted-fg)] inline-flex items-center gap-2"><FileDown className="h-3.5 w-3.5" /> CSV export is a Researcher feature.</div>
              )
            )}
          </div>
        </div>

        <div className="space-y-3" data-testid={MATCH.results}>
          <div className="flex items-end justify-between gap-2 sticky top-20 bg-[color:var(--bg)] py-1 z-10">
            <div>
              <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Ranked matches</div>
              <div className="text-sm text-[color:var(--muted-fg)]">{results ? `${results.matches.length} protocols` : 'Submit the form to see results.'}</div>
            </div>
          </div>

          {!results && !loading && (
            <Card data-testid={STATE.empty} className="p-8 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-color)] bg-[color:var(--surface)]">
              <div className="text-sm font-medium">No results yet</div>
              <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Fill in any biomarkers above and click “Run match”. Results are deterministic and inspectable.</p>
            </Card>
          )}

          {loading && (
            <div data-testid={STATE.loading} className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full rounded-[var(--radius-md)]" />)}
            </div>
          )}

          {results && results.matches.map((m, i) => (
            <ProtocolCard
              key={m.protocol.id}
              protocol={m.protocol}
              score={m.score}
              applicable={m.applicable}
              applicabilityNotes={m.applicability_notes}
              contraindicationNotes={m.contraindication_notes}
              scoreBreakdown={m.score_breakdown}
            />
          ))}

          {results && user?.role === 'user' && (
            <TierGateCTA title="Want to see full technical summaries?" description="Researcher tier unlocks technical summaries, mechanism deep-dives and CSV export." />
          )}
        </div>
      </div>
    </div>
  );
}
